import { directSupabase } from '../../../lib/supabaseClient';
import type { TemplateKind } from '../types/documents.types';

export interface DocumentTemplate {
  id: string;
  organisationId: string;
  documentType: TemplateKind;
  template: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentTemplateInput {
  organisationId: string;
  documentType: TemplateKind;
  template: string;
}

class DocumentTemplateService {
  private tableName = 'document_templates';

  async getTemplate(organisationId: string, documentType: TemplateKind): Promise<DocumentTemplate | null> {
    try {
      const { data, error } = await directSupabase
        .from(this.tableName)
        .select('*')
        .eq('organisation_id', organisationId)
        .eq('document_type', documentType)
        .single();

      if (error) {
        console.error('Error fetching template:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching template:', error);
      return null;
    }
  }

  async saveTemplate(template: DocumentTemplateInput): Promise<DocumentTemplate | null> {
    try {
      // Check if template already exists
      const existing = await this.getTemplate(template.organisationId, template.documentType);
      
      if (existing) {
        // Update existing template
        const { data, error } = await directSupabase
          .from(this.tableName)
          .update({
            template: template.template,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating template:', error);
          return null;
        }

        return data;
      } else {
        // Create new template
        const { data, error } = await directSupabase
          .from(this.tableName)
          .insert({
            organisation_id: template.organisationId,
            document_type: template.documentType,
            template: template.template
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating template:', error);
          return null;
        }

        return data;
      }
    } catch (error) {
      console.error('Error saving template:', error);
      return null;
    }
  }

  async getDefaultTemplate(documentType: TemplateKind): Promise<string> {
    if (documentType === 'CISCertificate') {
      return `<div class="document-template">
  <div class="header">
    <div class="header-content">
      <div class="title">CONSTRUCTION INDUSTRY SCHEME (CIS)</div>
      <div class="subtitle">Payment and Deduction Statement</div>
      <div class="date">Tax Month (6th–5th): {{taxMonthLabel}}</div>
    </div>
    <div class="logo-section">
      {{#logoPath}}
      <img src="{{logoPath}}" alt="Company Logo" class="logo" />
      {{/logoPath}}
    </div>
  </div>

  <div class="info-grid">
    <div class="info-section">
      <h3>Contractor Details</h3>
      <p>
        <strong>Contractor Name:</strong> {{contractorName}}<br />
        <strong>Trading Name (if different):</strong> {{contractorTradingName}}<br />
        <strong>Contractor UTR:</strong> {{contractorUtr}}<br />
        <strong>Company Registration Number (if Ltd):</strong> {{contractorCompanyReg}}<br />
        <strong>Address:</strong> {{contractorAddress}}<br />
        <strong>Postcode:</strong> {{contractorPostcode}}
      </p>
    </div>
    <div class="info-section">
      <h3>Subcontractor Details</h3>
      <p>
        <strong>Subcontractor Name:</strong> {{subcontractorName}}<br />
        <strong>Trading Name (if different):</strong> {{subcontractorTradingName}}<br />
        <strong>Subcontractor UTR:</strong> {{subcontractorUtr}}<br />
        <strong>National Insurance No. (if sole trader):</strong> {{subcontractorNi}}<br />
        <strong>Company Registration Number (if Ltd):</strong> {{subcontractorCompanyReg}}<br />
        <strong>Address:</strong> {{subcontractorAddress}}
      </p>
    </div>
  </div>

  <div class="description-section">
    <h3>Payment Details</h3>
    <p>
      <strong>Verification Number (if applicable):</strong> {{verificationNumber}}<br />
      <strong>Payment Date:</strong> {{paymentDate}}
    </p>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th>Description of Work</th>
        <th style="text-align:right;">Gross Amount (£)</th>
      </tr>
    </thead>
    <tbody>
      {{workRowsHtml}}
    </tbody>
  </table>

  <div class="description-section">
    <h3>Totals</h3>
    <p>
      <strong>Total Labour (Subject to CIS):</strong> {{totalLabour}}<br />
      <strong>Total Materials (Not Subject to CIS):</strong> {{totalMaterials}}<br />
      <strong>Other Adjustments (if any):</strong> {{otherAdjustments}}
    </p>
  </div>

  <div class="description-section">
    <h3>CIS Calculation</h3>
    <p>
      <strong>Gross Amount Subject to CIS:</strong> {{grossSubjectToCis}}<br />
      <strong>CIS Deduction Rate:</strong> {{cisRate0}} 0% &nbsp; {{cisRate20}} 20% &nbsp; {{cisRate30}} 30%<br />
      <strong>CIS Tax Deducted:</strong> {{cisTaxDeducted}}
    </p>
  </div>

  <div class="description-section">
    <h3>Summary</h3>
    <p>
      <strong>Total Gross Payment:</strong> {{totalGrossPayment}}<br />
      <strong>Less CIS Deduction:</strong> {{lessCisDeduction}}<br />
      <strong>Net Amount Paid:</strong> {{netAmountPaid}}
    </p>
  </div>

  <div class="description-section">
    <h3>Declaration</h3>
    <p>This statement is provided in accordance with the Construction Industry Scheme (CIS).</p>
    <p>The contractor confirms that the above deduction has been made and will be paid to HMRC.</p>
    <p>
      <strong>Signed:</strong> _______________________________<br />
      <strong>Name:</strong> ________________________________<br />
      <strong>Position:</strong> _____________________________<br />
      <strong>Date:</strong> {{generationDate}}
    </p>
  </div>
</div>`;
    }

    // Return a basic default template
    return `<div class="document-template">
      <!-- Header Section -->
      <div class="header">
        <div class="header-content">
          <div class="title">{{headerTitle || '${documentType}'}</div>
          <div class="subtitle">{{documentType}} - {{documentRef}}</div>
          <div class="date">Date: {{documentDate}}</div>
        </div>
        <div class="logo-section">
          {{#logoPath}}
          <img src="{{logoPath}}" alt="Company Logo" class="logo" />
          {{/logoPath}}
        </div>
      </div>

      <!-- Company Information -->
      <div class="info-grid">
        <div class="info-section">
          <h3>From</h3>
          <p>{{companyName}}<br />
          {{#companyAddress}}{{companyAddress}}<br />{{/companyAddress}}
          {{#companyPhone}}Phone: {{companyPhone}}<br />{{/companyPhone}}
          {{#companyEmail}}Email: {{companyEmail}}<br />{{/companyEmail}}
          {{#companyVatNumber}}VAT: {{companyVatNumber}}{{/companyVatNumber}}</p>
        </div>
        <div class="info-section">
          <h3>To</h3>
          <p>{{#customerTitle}}{{customerTitle}} {{/customerTitle}}{{customerName}}<br />
          {{#customerAddress}}{{customerAddress}}{{/customerAddress}}</p>
        </div>
      </div>

      <!-- Document Description -->
      {{#description}}
      <div class="description-section">
        <h3>Description</h3>
        {{description}}
      </div>
      {{/description}}

      <!-- Line Items Table -->
      {{#hasLineItems}}
      <table class="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {{#lineItems}}
          <tr>
            <td><strong>{{name}}</strong><br />
            {{#description}}<span class="item-description">{{description}}</span>{{/description}}</td>
            <td>{{quantity}}</td>
            <td>{{unit}}</td>
            <td>{{unitPrice}}</td>
            <td>{{lineTotal}}</td>
          </tr>
          {{/lineItems}}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4">Base Cost Total</td>
            <td>{{baseCostTotal}}</td>
          </tr>
          {{#showFees}}
          <tr>
            <td colspan="4">Admin Fee ({{adminPct}}%)</td>
            <td>{{adminFee}}</td>
          </tr>
          <tr>
            <td colspan="4">Contingency ({{contingencyPct}}%)</td>
            <td>{{contingency}}</td>
          </tr>
          <tr>
            <td colspan="4">Profit Margin ({{profitPct}}%)</td>
            <td>{{profit}}</td>
          </tr>
          {{/showFees}}
          {{#vatEnabled}}
          <tr>
            <td colspan="4">VAT ({{vatPct}}%)</td>
            <td>{{vatAmount}}</td>
          </tr>
          {{/vatEnabled}}
          <tr class="total-row">
            <td colspan="4"><strong>Total{{#vatEnabled}} (incl. VAT){{/vatEnabled}}</strong></td>
            <td><strong>{{grandTotal}}</strong></td>
          </tr>
        </tfoot>
      </table>
      {{/hasLineItems}}
    </div>`;
  }
}

export const documentTemplateService = new DocumentTemplateService();
